// "Menu at this stop": the part of the Truck Stops dialog (TruckStops.jsx) that
// sets what one stop sells, at what price, and how many are on hand
// (stopMenu / saveStopMenu, migration 145).
//
// The state lives in useStopMenuEditor so the dialog's one Save button can send
// the stop and its menu together. Styling follows truckStopsTheme.js: ink
// primary, amber accents, orange focus ring, no green / blue / indigo.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery, gql } from '@apollo/client'
import {
  Box, Button, Checkbox, CircularProgress, InputAdornment, TextField, Typography,
} from '@mui/material'
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import SearchIcon from '@mui/icons-material/Search'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { stopMenu as stopMenuQuery } from '../apollo'
import { TS, focusRing } from './truckStopsTheme'

const GET_STOP_MENU = gql`${stopMenuQuery}`

// Menus longer than this open with their categories folded.
const FOLD_ABOVE = 30

const asObject = (v) => {
  if (!v) return null
  if (typeof v === 'object') return v
  try { return JSON.parse(v) } catch (_) { return null }
}
const money = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v).toFixed(2))
const round2 = (n) => Math.round(Number(n) * 100) / 100

// Blank = not set. NaN = not a usable number.
const parsePrice = (s) => {
  const t = String(s ?? '').trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? round2(n) : NaN
}
const parseCount = (s) => {
  const t = String(s ?? '').trim()
  if (!t) return null
  const n = Number(t)
  return Number.isInteger(n) && n >= 0 ? n : NaN
}

const sizesOf = (item) => Object.keys(asObject(item.regularVariantPrices) || {}).filter((k) => k.trim())
// pricingMode is SINGLE | VARIANTS on advanced items.
const isVariantItem = (item) => sizesOf(item).length > 0 &&
  (String(item.pricingMode || '').toUpperCase().startsWith('VARIANT') || item.regularPrice == null)

const regularText = (item) => {
  if (isVariantItem(item)) {
    const vp = asObject(item.regularVariantPrices) || {}
    return sizesOf(item).map((s) => (money(vp[s]) != null ? `${s} $${money(vp[s])}` : `${s} no price`)).join(' · ')
  }
  return money(item.regularPrice) != null ? `$${money(item.regularPrice)}` : 'no price set'
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`

function draftFrom(menu) {
  return {
    useFullMenu: !!menu.useFullMenu,
    stockMode: menu.stockMode === 'RUNNING' ? 'RUNNING' : 'DAILY',
    rows: Object.fromEntries((menu.items || []).map((it) => {
      const typed = asObject(it.stopVariantPrices) || {}
      return [it.itemId, {
        // On the full menu every item is sold; switching to a custom menu
        // starts from all of them ticked.
        included: menu.useFullMenu ? true : it.included !== false,
        price: it.stopPrice != null ? String(it.stopPrice) : '',
        sizes: Object.fromEntries(sizesOf(it).map((s) => [s, typed[s] != null ? String(typed[s]) : ''])),
        stock: it.stockQuantity != null ? String(it.stockQuantity) : '',
      }]
    })),
  }
}

/**
 * Loads a stop's menu and keeps the owner's unsaved edits. stopId null = no
 * stop yet (a new stop) or the dialog is closed.
 */
export function useStopMenuEditor(stopId) {
  const { data, loading, error, refetch } = useQuery(GET_STOP_MENU, {
    variables: { stopId },
    skip: !stopId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
  const [menu, setMenu] = useState(null)
  const [draft, setDraft] = useState(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!stopId) {
      setMenu(null); setDraft(null); setDirty(false)
      return
    }
    const loaded = data && data.stopMenu
    if (loaded && String(loaded.stopId) === String(stopId)) {
      setMenu(loaded); setDraft(draftFrom(loaded)); setDirty(false)
    }
  }, [data, stopId])

  const ready = !!(stopId && menu && draft && String(menu.stopId) === String(stopId))
  const items = useMemo(() => (ready ? menu.items || [] : []), [ready, menu])

  const setRow = useCallback((itemId, patch) => {
    setDraft((d) => ({ ...d, rows: { ...d.rows, [itemId]: { ...d.rows[itemId], ...patch } } }))
    setDirty(true)
  }, [])
  const setRows = useCallback((itemIds, patch) => {
    setDraft((d) => {
      const rows = { ...d.rows }
      itemIds.forEach((id) => { rows[id] = { ...rows[id], ...patch } })
      return { ...d, rows }
    })
    setDirty(true)
  }, [])
  const setMode = useCallback((patch) => {
    setDraft((d) => ({ ...d, ...patch }))
    setDirty(true)
  }, [])
  // After saveStopMenu: what the server now holds becomes the starting point.
  const loaded = useCallback((saved) => {
    setMenu(saved); setDraft(draftFrom(saved)); setDirty(false)
  }, [])

  // → { variables } for saveStopMenu, or { error } to show the owner.
  const build = () => {
    if (!ready) return { variables: null }
    const out = []
    for (const it of items) {
      const r = draft.rows[it.itemId] || {}
      const stock = parseCount(r.stock)
      if (Number.isNaN(stock)) {
        return { error: `${it.name}: the on-hand count must be a whole number, or blank for no limit.` }
      }
      if (draft.useFullMenu) {
        // Full menu: the rows only carry counts, and the rows sent are all of them.
        if (stock != null) out.push({ itemId: it.itemId, stockQuantity: stock })
        continue
      }
      if (!r.included) continue
      const row = { itemId: it.itemId, included: true, stockQuantity: stock }
      if (isVariantItem(it)) {
        const regular = asObject(it.regularVariantPrices) || {}
        const typed = {}
        for (const s of sizesOf(it)) {
          const p = parsePrice(r.sizes && r.sizes[s])
          if (Number.isNaN(p)) return { error: `${it.name} (${s}): type a price like 8.50, or leave it blank for the regular price.` }
          if (p != null) typed[s] = p
        }
        if (Object.keys(typed).length) {
          // A size left blank keeps the regular price shown beside it, rather
          // than whatever the stop menu's general rule would make it.
          const all = {}
          for (const s of sizesOf(it)) {
            if (typed[s] != null) all[s] = typed[s]
            else if (money(regular[s]) != null) all[s] = round2(regular[s])
          }
          row.variantPrices = all
        }
      } else {
        const p = parsePrice(r.price)
        if (Number.isNaN(p)) return { error: `${it.name}: type a price like 8.50, or leave it blank for the regular price.` }
        if (p != null) row.price = p
      }
      out.push(row)
    }
    if (!draft.useFullMenu && !out.length) {
      return { error: 'Tick at least one item this stop sells, or switch it to the full menu.' }
    }
    return { variables: { stopId, useFullMenu: draft.useFullMenu, stockMode: draft.stockMode, items: out } }
  }

  return {
    stopId,
    ready,
    loading: !!stopId && !ready && loading,
    error: !!stopId && !ready && !loading ? error : null,
    refetch,
    items,
    draft,
    dirty,
    setRow,
    setRows,
    setMode,
    loaded,
    build,
  }
}

// ---- Shared controls (also used by TruckStops.jsx) ----

// A toggle pill: ink when on, white with a border when off. aria-pressed
// carries the state as well as the colour.
export const PillButton = ({ selected, children, sx, ...rest }) => (
  <Box
    component="button" type="button" aria-pressed={!!selected} {...rest}
    sx={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1,
      minHeight: 44, px: 2, boxSizing: 'border-box', borderRadius: '999px',
      font: 'inherit', fontSize: 15, fontWeight: selected ? 700 : 600, lineHeight: 1.3, cursor: 'pointer',
      border: `1.5px solid ${selected ? TS.text : TS.btnBorder}`,
      bgcolor: selected ? TS.text : TS.card, color: selected ? '#FFFFFF' : TS.label,
      transition: 'background-color 150ms ease, border-color 150ms ease',
      '&:hover': { bgcolor: selected ? TS.primaryHover : TS.page },
      '&:focus-visible': focusRing,
      ...sx
    }}
  >
    {children}
  </Box>
)

// One of the two "what does this stop sell" cards.
const ChoiceCard = ({ selected, onClick, title, desc }) => (
  <Box
    component="button" type="button" onClick={onClick} aria-pressed={!!selected}
    sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'left',
      width: '100%', boxSizing: 'border-box', font: 'inherit', cursor: 'pointer', borderRadius: '12px',
      // 2px border when selected, so 1px less padding keeps the size.
      p: selected ? '13px 15px' : '14px 16px',
      border: selected ? `2px solid ${TS.text}` : `1px solid ${TS.btnBorder}`,
      bgcolor: selected ? '#FFFBF2' : TS.card, color: TS.text,
      transition: 'border-color 150ms ease, background-color 150ms ease',
      '&:hover': selected ? {} : { borderColor: TS.inputBorder },
      '&:focus-visible': focusRing
    }}
  >
    <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}>{title}</Typography>
    <Typography component="span" sx={{ display: 'block', mt: 0.25, fontSize: 13.5, lineHeight: 1.45, color: TS.secondary }}>{desc}</Typography>
  </Box>
)

const LABEL = { fontSize: 13, fontWeight: 600, color: TS.label, lineHeight: 1.4, mb: '6px' }
const HELPER = { fontSize: 13, lineHeight: 1.45, color: TS.secondary, mt: '6px' }
// Item fields: 44px tall rather than the page's 48, so long menus stay compact.
const ROW_FIELD = { '& .MuiOutlinedInput-input': { height: 44 } }
const MONEY_FIELD = {
  ...ROW_FIELD,
  '& .MuiOutlinedInput-input': { height: 44, pl: 0 },
  '& .MuiInputAdornment-root .MuiTypography-root': { color: TS.secondary, fontWeight: 600 }
}

const ItemRow = React.memo(function ItemRow({ item, row, custom, setRow }) {
  const variant = isVariantItem(item)
  const regularSizes = asObject(item.regularVariantPrices) || {}
  const nameId = `stop-menu-item-${item.itemId}`
  const included = !custom || !!row.included
  const dollar = { startAdornment: <InputAdornment position="start">$</InputAdornment> }

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, borderTop: `1px solid ${TS.border}`, bgcolor: TS.card }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {custom && (
          <Checkbox
            checked={!!row.included}
            onChange={(e) => setRow(item.itemId, { included: e.target.checked })}
            inputProps={{ 'aria-labelledby': nameId }}
            sx={{ ml: -1, flexShrink: 0 }}
          />
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography id={nameId} sx={{
            fontSize: 15, fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere',
            color: included ? TS.text : TS.secondary
          }}>
            {item.name}
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: TS.secondary, overflowWrap: 'anywhere' }}>
            {`Regular ${regularText(item)}`}
            {item.stockLeft != null ? ` · ${item.stockLeft} left` : ''}
          </Typography>
        </Box>
      </Box>

      {included && (
        <Box sx={{
          mt: 1.25, display: 'grid', columnGap: 1.25, rowGap: 1.5,
          // 116px lets a price and a count sit side by side on a 390px phone.
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 116px), 1fr))',
          pl: custom ? { xs: 0, sm: '34px' } : 0
        }}>
          {custom && !variant && (
            <TextField
              label="Stop price" size="small" value={row.price || ''}
              placeholder={money(item.regularPrice) || ''}
              onChange={(e) => setRow(item.itemId, { price: e.target.value })}
              InputProps={dollar}
              inputProps={{ inputMode: 'decimal', 'aria-describedby': nameId }}
              sx={MONEY_FIELD}
            />
          )}
          {custom && variant && sizesOf(item).map((s) => (
            <TextField
              key={s}
              label={`${s} price`} size="small" value={(row.sizes && row.sizes[s]) || ''}
              placeholder={money(regularSizes[s]) || ''}
              onChange={(e) => setRow(item.itemId, { sizes: { ...(row.sizes || {}), [s]: e.target.value } })}
              InputProps={dollar}
              inputProps={{ inputMode: 'decimal', 'aria-describedby': nameId }}
              sx={MONEY_FIELD}
            />
          ))}
          <TextField
            label="On hand" size="small" value={row.stock || ''} placeholder="No limit"
            onChange={(e) => setRow(item.itemId, { stock: e.target.value })}
            inputProps={{ inputMode: 'numeric', 'aria-describedby': nameId }}
            sx={ROW_FIELD}
          />
        </Box>
      )}
    </Box>
  )
})

function CategoryGroup({ name, items, allItems, rows, custom, open, onToggle, setRow, setRows }) {
  const ids = allItems.map((it) => it.itemId)
  const ticked = allItems.filter((it) => rows[it.itemId] && rows[it.itemId].included).length
  const all = ticked === allItems.length
  const headId = `stop-menu-cat-${name.replace(/[^a-z0-9]+/gi, '-')}`

  return (
    <Box sx={{ '&:not(:first-of-type)': { borderTop: `1px solid ${TS.border}` } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: custom ? 0.5 : 1, pr: 1, py: 0.5, bgcolor: TS.page }}>
        {/* Ticked only when every item is; a part-ticked category is said in
            words ("3 of 8 sold here") rather than with the dash glyph. */}
        {custom && (
          <Checkbox
            checked={all}
            onChange={() => setRows(ids, { included: !all })}
            inputProps={{ 'aria-label': `Sell everything in ${name} at this stop` }}
            sx={{ flexShrink: 0 }}
          />
        )}
        <Box
          component="button" type="button" onClick={onToggle} aria-expanded={open} id={headId}
          sx={{
            flex: 1, minWidth: 0, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
            border: 0, bgcolor: 'transparent', font: 'inherit', color: TS.text, cursor: 'pointer', textAlign: 'left',
            px: 1, borderRadius: '8px', '&:focus-visible': focusRing
          }}
        >
          <Box component="span" sx={{ minWidth: 0 }}>
            <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 700, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
              {name}
            </Typography>
            <Typography component="span" sx={{ display: 'block', fontSize: 13, color: TS.secondary }}>
              {custom ? `${ticked} of ${allItems.length} sold here` : plural(allItems.length, 'item', 'items')}
            </Typography>
          </Box>
          <KeyboardArrowDownRoundedIcon aria-hidden sx={{
            color: TS.secondary, flexShrink: 0, transition: 'transform 150ms ease',
            transform: open ? 'rotate(180deg)' : 'none'
          }} />
        </Box>
      </Box>
      {open && (
        <Box role="group" aria-labelledby={headId}>
          {items.map((it) => (
            <ItemRow key={it.itemId} item={it} row={rows[it.itemId] || {}} custom={custom} setRow={setRow} />
          ))}
        </Box>
      )}
    </Box>
  )
}

/**
 * isNew: the stop isn't saved yet, so there is nothing to attach a menu to.
 * onCreateAndOpen saves it and keeps the dialog open on this section.
 */
export default function StopMenuSection({ editor, isNew, canCreate, creating, onCreateAndOpen, expanded, setExpanded }) {
  const [query, setQuery] = useState('')
  const [openCats, setOpenCats] = useState(null)
  const { ready, items, draft } = editor

  // Group by category, keeping the server's order.
  const groups = useMemo(() => {
    const map = new Map()
    items.forEach((it) => {
      const key = it.categoryName || 'Other items'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(it)
    })
    return [...map.entries()].map(([name, list]) => ({ name, items: list }))
  }, [items])

  // Short menus open with every category showing; long ones folded.
  useEffect(() => {
    if (!ready) { setOpenCats(null); return }
    setOpenCats((prev) => prev || new Set(items.length <= FOLD_ABOVE ? groups.map((g) => g.name) : []))
  }, [ready, items.length, groups])

  const custom = ready && !draft.useFullMenu
  const counted = ready ? items.filter((it) => String((draft.rows[it.itemId] || {}).stock || '').trim() !== '').length : 0
  const tickedCount = ready ? items.filter((it) => (draft.rows[it.itemId] || {}).included).length : 0
  const priced = ready ? items.filter((it) => {
    const r = draft.rows[it.itemId] || {}
    return r.included && (String(r.price || '').trim() || Object.values(r.sizes || {}).some((v) => String(v || '').trim()))
  }).length : 0

  let summary = 'Sells your full menu until you change it.'
  if (ready) {
    const parts = draft.useFullMenu
      ? ['Full menu at regular prices']
      : [`Own menu · ${tickedCount} of ${plural(items.length, 'item', 'items')}`]
    if (custom && priced) parts.push(`${priced} at stop prices`)
    if (counted) parts.push(`${plural(counted, 'item', 'items')} counted`)
    summary = parts.join(' · ')
  }

  const q = query.trim().toLowerCase()
  const shownGroups = q
    ? groups
      .map((g) => ({ ...g, all: g.items, items: g.items.filter((it) => (it.name || '').toLowerCase().includes(q)) }))
      .filter((g) => g.items.length)
    : groups.map((g) => ({ ...g, all: g.items }))

  const toggleCat = (name) => setOpenCats((prev) => {
    const next = new Set(prev || [])
    if (next.has(name)) next.delete(name)
    else next.add(name)
    return next
  })

  let body = null
  if (isNew) {
    body = (
      <>
        <Box sx={{ mt: 2, p: '14px 16px', borderRadius: '12px', bgcolor: TS.page, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <LockOutlinedIcon aria-hidden sx={{ fontSize: 20, mt: '2px', color: TS.secondary, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: TS.label }}>
            Save the stop first. Then you can pick what it sells here, type stop-only prices and set how many you have on hand.
          </Typography>
        </Box>
        <Button
          variant="outlined" onClick={onCreateAndOpen} disabled={!canCreate || creating}
          sx={{ mt: 1.5, maxWidth: '100%', height: 'auto', minHeight: 46, py: 1 }}
        >
          {creating ? <CircularProgress size={18} sx={{ color: TS.secondary }} /> : 'Add stop and set its menu'}
        </Button>
        {!canCreate && <Typography sx={HELPER}>Fill in the address above first.</Typography>}
      </>
    )
  } else if (editor.loading) {
    body = (
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CircularProgress size={20} />
        <Typography sx={{ fontSize: 14, color: TS.secondary }}>Loading this stop’s menu…</Typography>
      </Box>
    )
  } else if (editor.error) {
    body = (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: 14, color: TS.danger, overflowWrap: 'anywhere' }}>
          {`Couldn’t load this stop’s menu. ${editor.error?.graphQLErrors?.[0]?.message || editor.error?.message || ''}`.trim()}
        </Typography>
        <Button variant="outlined" size="small" onClick={() => editor.refetch()} sx={{ mt: 1.25 }}>Try again</Button>
      </Box>
    )
  } else if (ready && !items.length) {
    body = (
      <Typography sx={{ mt: 2, fontSize: 14, lineHeight: 1.5, color: TS.label }}>
        Your regular menus have no items yet. Add items under Menus, then come back to choose what this stop sells.
      </Typography>
    )
  } else if (ready && !expanded) {
    body = (
      <Button variant="outlined" onClick={() => setExpanded(true)} sx={{ mt: 2, maxWidth: '100%', height: 'auto', minHeight: 46, py: 1 }}>
        Choose items, prices and counts
      </Button>
    )
  } else if (ready) {
    const daily = draft.stockMode !== 'RUNNING'
    body = (
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <Box>
          <Typography id="stop-menu-mode" sx={LABEL}>What it sells</Typography>
          <Box role="group" aria-labelledby="stop-menu-mode" sx={{
            display: 'grid', gap: 1.25,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }
          }}>
            <ChoiceCard
              selected={draft.useFullMenu}
              onClick={() => editor.setMode({ useFullMenu: true })}
              title="Full menu"
              desc="Everything on your regular menus, at regular prices."
            />
            <ChoiceCard
              selected={!draft.useFullMenu}
              onClick={() => editor.setMode({ useFullMenu: false })}
              title="Custom menu"
              desc="Only the items you tick, at stop prices if you type them."
            />
          </Box>
        </Box>

        <Box>
          <Typography id="stop-menu-restock" sx={LABEL}>On-hand counts restock</Typography>
          <Box role="group" aria-labelledby="stop-menu-restock" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <PillButton selected={daily} onClick={() => editor.setMode({ stockMode: 'DAILY' })}>Every day it runs</PillButton>
            <PillButton selected={!daily} onClick={() => editor.setMode({ stockMode: 'RUNNING' })}>One running total</PillButton>
          </Box>
          <Typography sx={HELPER}>
            {daily
              ? 'Each day this stop runs starts again from the counts below.'
              : 'Orders keep counting down until you type a new number, which restocks it.'}
            {' Leave a count blank for no limit.'}
          </Typography>
        </Box>

        {custom && (
          <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: TS.label }}>
            Tick what this stop sells. A stop price is optional: leave it blank and the item keeps its regular price.
          </Typography>
        )}

        <TextField
          size="small" placeholder="Find an item" value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: TS.secondary, fontSize: 20 }} /></InputAdornment> }}
          inputProps={{ 'aria-label': 'Find an item' }}
          sx={{ '& .MuiOutlinedInput-input': { pl: 0 } }}
        />

        <Box sx={{ border: `1px solid ${TS.border}`, borderRadius: '12px', overflow: 'hidden' }}>
          {shownGroups.length === 0 ? (
            <Typography sx={{ p: 2, fontSize: 14, color: TS.secondary }}>No items match “{query.trim()}”.</Typography>
          ) : shownGroups.map((g) => (
            <CategoryGroup
              key={g.name}
              name={g.name}
              items={g.items}
              allItems={g.all}
              rows={draft.rows}
              custom={custom}
              open={!!q || !!(openCats && openCats.has(g.name))}
              onToggle={() => toggleCat(g.name)}
              setRow={editor.setRow}
              setRows={editor.setRows}
            />
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Box component="section" aria-labelledby="stop-menu-heading" sx={{
      border: `1px solid ${TS.border}`, borderRadius: '14px', p: { xs: '16px', sm: '18px 20px' }, minWidth: 0
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box aria-hidden sx={{
          width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', flexShrink: 0,
          bgcolor: TS.amberSoft, color: TS.amberIcon
        }}>
          <RestaurantMenuOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography id="stop-menu-heading" component="h3" sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.35 }}>
            Menu at this stop
          </Typography>
          <Typography sx={{ fontSize: 14, lineHeight: 1.45, color: TS.secondary, mt: 0.25, overflowWrap: 'anywhere' }}>
            {summary}
          </Typography>
          {editor.dirty && (
            <Typography sx={{ fontSize: 13, lineHeight: 1.45, color: TS.amberText, fontWeight: 600, mt: 0.5 }}>
              Not saved yet. Press Save stop to keep these menu changes.
            </Typography>
          )}
        </Box>
        {ready && items.length > 0 && expanded && (
          <Button size="small" onClick={() => setExpanded(false)} sx={{ flexShrink: 0 }}>Hide</Button>
        )}
      </Box>
      {body}
    </Box>
  )
}
